import axios from 'axios'
import Swal from 'sweetalert2'

import { resolveLanguageName } from '../common/functions/resolveLanguageName'
import { config } from '../config'
import { HTTP_STATUS, PLATFORM } from '../constants'
import { clearAllData, getAccessToken, remoteLogin } from '../utils'
import { getSelectedBranchMeta } from '../utils/userStorage'

class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: config.api.baseURL,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  init(tokenStorage = {}) {
    this.tokenStorage = { ...this.tokenStorage, ...tokenStorage }
    return this
  }

  setupInterceptors() {
    this.client.interceptors.request.use(
      (request) => {
        const token = getAccessToken()
        if (token && request.headers) {
          request.headers.Authorization = token
        }
        // Attach `branchId` + `Portal` only after a branch is picked. Both
        // headers are intentionally skipped on login / me / refresh so the
        // auth endpoints behave exactly like they did before the auth flow
        // was scoped.
        try {
          const meta = getSelectedBranchMeta()
          const fullUrl = `${request.url || ''}`
          // A caller-supplied `branch-id` wins over the selected branch —
          // the permission endpoint is called for a branch the user is
          // about to switch to, before it becomes the selected one.
          if (meta && request.headers && !request.headers['branch-id']) {
            if (meta.id !== undefined && meta.id !== null) {
              if (!fullUrl.includes('api/login/global')) {
                request.headers['branch-id'] = String(meta.id)
              }
            } else if (meta.uuid) {
              request.headers['branch-id'] = meta.uuid
            }
          }
          if (!fullUrl.includes('api/login/global')) {
            request.headers['Platform'] = PLATFORM.GUARDIAN
          }
        } catch {
          // getSelectedBranchMeta reads localStorage; safe to swallow on SSR
          // or any environment where localStorage is undefined.
        }

        if (token && request.headers && typeof window !== 'undefined') {
          try {
            const code = window.localStorage.getItem('mdg.preferredLanguage') || 'en'
            request.headers['language'] = resolveLanguageName(code)
          } catch {
            request.headers['language'] = 'english'
          }
        }
        return request
      },
      (error) => Promise.reject(error)
    )

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config
        const isLoginGlobal = (originalRequest?.url || '').includes('api/login/global')
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED && !originalRequest._retry) {
          // For the login endpoint, a 401 just means "wrong credentials".
          // Skip the swal modal + token-refresh dance and let the login form
          // surface the error inline. Other status codes from this endpoint
          // still fall through to the modal handlers below.
          if (isLoginGlobal) {
            return Promise.reject(error)
          }
          originalRequest._retry = false

          // Remote-login session (super-admin opened this tab via the
          // Remote Login button). The bearer is a 1h JWT and there is
          // no cross-origin refresh path, so a 401 here means the
          // admin's grant has lapsed. Don't redirect to /login — that
          // tab never belonged to a normal user — surface a modal
          // asking them to bounce back to the super-admin instead.
          // See step (3) of MDG-215.
          if (remoteLogin.isActive()) {
            clearAllData()
            remoteLogin.clear()
            await Swal.fire({
              title: 'Remote session expired',
              text: 'Your remote admin session has ended. Return to the super-admin to start a new session.',
              icon: 'warning',
              iconColor: '#ffa65c',
              background: '#131c30',
              color: '#EEF3FB',
              confirmButtonText: 'Return to Admin',
              confirmButtonColor: '#3B82F6',
              allowOutsideClick: false,
              allowEscapeKey: false,
              didOpen: (popup) => {
                const container = popup?.closest('.swal2-container')
                if (container) container.style.zIndex = '2000'
              },
            })
            // window.close() is often blocked when the tab wasn't script-
            // opened; redirect is the reliable cross-browser fallback.
            const origin = config.app?.superAdminOrigin
            window.location.href = origin ? `${origin.replace(/\/+$/, '')}/customers` : '/login'
            return
          }

          clearAllData()
          await Swal.fire({
            title: 'Session expired',
            text: 'Your session has ended. You will be redirected to the login page.',
            icon: 'warning',
            iconColor: '#ffa65c',
            background: '#131c30',
            color: '#EEF3FB',
            confirmButtonText: 'Login',
            confirmButtonColor: '#3B82F6',
            allowOutsideClick: false,
            allowEscapeKey: false,
            // Sit above any open MUI dialog (theme.zIndex.modal = 1250,
            // backdrop = 1249). Without this, a 401 fired while a modal
            // is open leaves the Swal hidden and the modal stuck.
            didOpen: (popup) => {
              const container = popup?.closest('.swal2-container')
              if (container) container.style.zIndex = '2000'
            },
          })
          window.location.href = '/login'
        } else if (error.response?.status === HTTP_STATUS.SERVER_ERROR) {
          // Caller has its own error UI (e.g. a dialog that surfaces
          // the error inline) — skip the global popup so we don't stack
          // two error modals on top of each other. Caller signals this
          // by passing `silentError: true` in the request config.
          if (originalRequest?.silentError) {
            return Promise.reject(error)
          }
          const message =
            error.response?.data?.status?.message ||
            error.response?.data?.message ||
            'Something went wrong on our end. Please try again later.'
          await Swal.fire({
            title: 'Server error',
            text: message,
            icon: 'error',
            iconColor: '#ff3d57',
            background: '#131c30',
            color: '#EEF3FB',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3B82F6',
            // Same z-index rationale as the 401 path — see comment above.
            didOpen: (popup) => {
              const container = popup?.closest('.swal2-container')
              if (container) container.style.zIndex = '2000'
            },
          })
        }
        return Promise.reject(error)
      }
    )
  }

  get(url, options = {}) {
    return this.client.get(url, options)
  }

  post(url, data, options = {}) {
    return this.client.post(url, data, options)
  }

  put(url, data, options = {}) {
    return this.client.put(url, data, options)
  }

  delete(url, options = {}) {
    return this.client.delete(url, options)
  }

  patch(url, data, options = {}) {
    return this.client.patch(url, data, options)
  }
}

export const apiClient = new ApiClient()
